import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { React, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui

import { Stack, Link, Typography, IconButton, Alert, Divider, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import 'react-dropzone-uploader/dist/styles.css';
import { OpenInNew } from '@mui/icons-material';
import Label from '../../../components/Label';
import { FormProvider } from '../../../components/hook-form';
import RHFFileInput from '../../../components/hook-form/RHFFileInput';
import { useApi } from '../../../hooks/useApi';
import tracker from '../../../api/tracker';
import useNotification from '../../../hooks/useNotificaction';

// ----------------------------------------------------------------------

export default function DocRequestForm({ docRequest }) {
  const { t } = useTranslation();
  const { doSave } = useApi();
  const [sendNotification] = useNotification();
  const [isOpeningDocument, setIsOpeningDocument] = useState(false);
  const [unpaidConfirmOpen, setUnpaidConfirmOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  const ModelSchema = Yup.object().shape({});

  const defaultValues = {
    title: '',
  };

  const methods = useForm({
    resolver: yupResolver(ModelSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (docRequest) {
      const fields = ['phone', 'email', 'created_at'];
      fields.forEach((field) => setValue(field, docRequest[field]));
    }
  }, [docRequest, setValue]);

  const submitUpload = async (data) => {
    if (!data.document || !data.document[0]) {
      sendNotification({ msg: t('document_upload'), variant: 'warning' });
      return;
    }

    data.document = data.document[0];
    const headers = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    await doSave(
      'docRequests',
      data,
      t('doc_request_sent'),
      `/dashboard/docRequest`,
      docRequest,
      headers,
      null,
      '/uploadFile'
    );
  };

  const onSubmit = async (data) => {
    if (!docRequest?.donation_made) {
      setPendingSubmit(data);
      setUnpaidConfirmOpen(true);
      return;
    }
    await submitUpload(data);
  };

  const confirmUnpaidSend = async () => {
    setUnpaidConfirmOpen(false);
    if (pendingSubmit) {
      await submitUpload(pendingSubmit);
      setPendingSubmit(null);
    }
  };

  const handleViewDocument = async () => {
    if (!docRequest?.hashid) return;
    setIsOpeningDocument(true);
    try {
      const res = await tracker.get(`docRequests/${docRequest.hashid}/document`);
      const url = res.data?.data?.url;
      if (!url) {
        throw new Error('missing url');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('document_not_found'),
        variant: 'error',
      });
    } finally {
      setIsOpeningDocument(false);
    }
  };

  DocRequestForm.propTypes = {
    docRequest: PropTypes.shape({
      hashid: PropTypes.string,
      document_sent: PropTypes.any,
      document: PropTypes.any,
      donation_made: PropTypes.bool,
    }),
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!docRequest?.donation_made && (
          <Alert severity="warning" variant="outlined">
            {t('send_document_unpaid_warning')}
          </Alert>
        )}
        {docRequest && docRequest.document_sent && (
          <>
            <Alert severity={'info'}> {t('document_sent')}</Alert>
            <Typography variant="subtitle1">
              <strong>{t('sent_document')}</strong>:{' '}
              <Tooltip title={t('view_document')}>
                <span>
                  <IconButton
                    onClick={handleViewDocument}
                    disabled={isOpeningDocument}
                    color="primary"
                    aria-label={t('view_document')}
                  >
                    <OpenInNew />
                  </IconButton>
                </span>
              </Tooltip>
            </Typography>
            <Divider />
          </>
        )}
        <div style={{ textAlign: 'center' }}>
          <Label sx={{ marginBottom: '5px' }}>{t('document_upload')}</Label>
          <RHFFileInput name="document" />
        </div>
        <input type="hidden" name="id" />
        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          color={docRequest?.donation_made ? 'primary' : 'warning'}
          loading={isSubmitting}
        >
          {t('Upload_and_Send')}
        </LoadingButton>
        <Link component={RouterLink} variant="subtitle2" underline="hover" to="/dashboard/docRequest">
          {t('go_back')}
        </Link>
      </Stack>
      <Dialog open={unpaidConfirmOpen} onClose={() => setUnpaidConfirmOpen(false)}>
        <DialogTitle>{t('send_document_unpaid_title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{t('send_document_unpaid_warning')}</Alert>
          <DialogContentText>{t('send_document_unpaid_confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpaidConfirmOpen(false)}>{t('cancel_action')}</Button>
          <LoadingButton color="warning" variant="contained" loading={isSubmitting} onClick={confirmUnpaidSend}>
            {t('send_document_anyway')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
